import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./LessonReportNoteCell.css";

type Props = {
  note: string | null | undefined;
};

type PopoverPos = { top: number; left: number };

export default function LessonReportNoteCell({ note }: Props) {
  const text = note?.trim() || "";
  const popoverId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const open = hovered || pinned;

  const updatePos = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const popoverW = 280;
    const gap = 8;
    let left = rect.right - popoverW;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= 180
        ? rect.bottom + gap
        : Math.max(8, rect.top - gap - 160);
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, text]);

  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setPinned(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setPinned(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  if (!text) {
    return <span className="lr-note-cell lr-note-cell--empty" aria-hidden>—</span>;
  }

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="tooltip"
            className="lr-note-cell__popover lr-note-cell__popover--floating"
            style={{ top: pos.top, left: pos.left }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <strong className="lr-note-cell__popover-title">Ders Notu</strong>
            <p className="lr-note-cell__popover-text">{text}</p>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`lr-note-cell${open ? " lr-note-cell--open" : ""}${pinned ? " lr-note-cell--pinned" : ""}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <button
          ref={btnRef}
          type="button"
          className="lr-note-cell__btn"
          aria-label="Ders Notu"
          aria-expanded={open}
          aria-controls={popoverId}
          onClick={() => setPinned((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
            <path
              fill="currentColor"
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm0 2.5L18.5 9H14V4.5zM8 13h8v1.5H8V13zm0 3.5h8V18H8v-1.5z"
            />
          </svg>
        </button>
      </div>
      {popover}
    </>
  );
}
